import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Info, Search } from "lucide-react";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { productArt } from "@/components/ProductArt";
import {
  bestOffer,
  categories,
  products,
  worstOffer,
  type Category,
} from "@/lib/products";

type SearchParams = {
  category?: Category;
};

export const Route = createFileRoute("/preisvergleich")({
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const raw = search.category;
    const valid = categories.some((c) => c.id === raw);
    return { category: valid ? (raw as Category) : undefined };
  },
  head: () => ({
    meta: [
      { title: "Preisvergleich — Elektronik, Handys & Computer" },
      {
        name: "description",
        content:
          "Vergleiche Preise für Elektronik, Smartphones und Computer bei mehreren Händlern und finde das beste Angebot.",
      },
      { property: "og:title", content: "Preisvergleich" },
      {
        property: "og:description",
        content: "Elektronik, Handys und Computer im Preisvergleich.",
      },
    ],
  }),
  component: PreisvergleichPage,
});

const currency = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
});

function PreisvergleichPage() {
  const { category } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [active, setActive] = useState<Category | "alle">(category ?? "alle");
  const [query, setQuery] = useState("");

  const selectCategory = (c: Category | "alle") => {
    setActive(c);
    void navigate({
      search: c === "alle" ? {} : { category: c },
      replace: true,
    });
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const matchesCategory = active === "alle" || p.category === active;
      const matchesQuery =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [active, query]);

  return (
    <main className="relative">
      <SiteNav />

      <Hero
        eyebrow="Preisvergleich"
        title={
          <>
            Elektronik, Handys &amp; Computer
            <span className="text-primary"> im Vergleich.</span>
          </>
        }
        subtitle="Wir stellen Angebote verschiedener Händler nebeneinander — transparent, übersichtlich, aktuell."
      />

      <section className="relative pb-24 px-6">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="mb-8 flex items-start gap-3 rounded-xl border border-foreground/12 bg-card px-4 py-3 text-sm text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <p>
                Beispielhafte Preise zur Veranschaulichung — noch keine
                Live-Daten von Händlern.
              </p>
            </div>
          </Reveal>

          <Reveal>
            <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => selectCategory("alle")}
                  className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-colors ${
                    active === "alle"
                      ? "bg-foreground text-background border-foreground"
                      : "border-foreground/15 text-muted-foreground hover:text-foreground hover:border-foreground/40"
                  }`}
                >
                  Alle
                </button>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => selectCategory(c.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-colors ${
                      active === c.id
                        ? "bg-foreground text-background border-foreground"
                        : "border-foreground/15 text-muted-foreground hover:text-foreground hover:border-foreground/40"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Produkt oder Marke suchen…"
                  className="w-full rounded-full border-2 border-foreground/15 bg-card py-2 pl-9 pr-4 text-sm outline-none focus:border-primary transition-colors"
                />
              </div>
            </div>
          </Reveal>

          {filtered.length === 0 ? (
            <Reveal>
              <p className="py-20 text-center text-muted-foreground">
                Keine Produkte gefunden. Versuch einen anderen Suchbegriff.
              </p>
            </Reveal>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((p, i) => {
                const best = bestOffer(p.offers);
                const worst = worstOffer(p.offers);
                const savings = worst.price - best.price;
                const Art = productArt[p.art];
                const sorted = [...p.offers].sort((a, b) => a.price - b.price);

                return (
                  <Reveal key={p.id} delay={(i % 3) * 100}>
                    <div className="relative h-full rounded-2xl border border-foreground/12 bg-card p-6 pt-8 ticket-shadow">
                      <span className="punch-hole" />

                      {savings > 0 && (
                        <span className="absolute -top-3 right-5 stamp text-accent">
                          Bestpreis
                        </span>
                      )}

                      <div className="flex items-center gap-4">
                        <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl border border-foreground/12 bg-background text-foreground">
                          <Art className="h-9 w-9" tone="tag" />
                        </div>
                        <div>
                          <div className="text-xs font-mono uppercase tracking-[0.14em] text-muted-foreground">
                            {p.brand}
                          </div>
                          <h3 className="font-display font-semibold leading-tight text-foreground">
                            {p.name}
                          </h3>
                        </div>
                      </div>

                      <div className="my-5 text-foreground/20">
                        <div className="barcode-rule" />
                      </div>

                      <div className="space-y-2.5">
                        {sorted.map((o, oi) => (
                          <div
                            key={o.retailer}
                            className="flex items-baseline gap-2 text-sm"
                          >
                            <span
                              className={
                                oi === 0
                                  ? "font-semibold text-foreground"
                                  : "text-muted-foreground"
                              }
                            >
                              {o.retailer}
                            </span>
                            <span className="flex-1 border-b border-dotted border-foreground/25 translate-y-[-3px]" />
                            <span
                              className={`font-mono tabular-nums ${
                                oi === 0
                                  ? "font-bold text-accent"
                                  : "text-foreground/70"
                              }`}
                            >
                              {currency.format(o.price)}
                            </span>
                          </div>
                        ))}
                      </div>

                      {savings > 0 && (
                        <p className="mt-5 text-xs text-muted-foreground">
                          Sparst bis zu{" "}
                          <span className="font-mono font-semibold text-accent">
                            {currency.format(savings)}
                          </span>{" "}
                          im Vergleich.
                        </p>
                      )}
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}
